import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import DesktopLayout from "@/components/Layout/DesktopLayout";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Search, Filter, User, ArrowUpDown, RotateCcw } from "lucide-react";
import Pagination, { usePaged } from "@/components/Pagination";
import { PROJECT_STAGES_BY_KEY } from "@/lib/workflow";

const STAGE_COLORS = {
  payment_verification:    { color: "#0F172A", bg: "#e2e8f0" },
  design_drawings:         { color: "#0284C7", bg: "#dbeafe" },
  customer_approval:       { color: "#c2410c", bg: "#ffedd5" },
  production_coordination: { color: "#7c3aed", bg: "#ede9fe" },
  production:              { color: "#F97316", bg: "#ffedd5" },
  pre_installation:        { color: "#DB2777", bg: "#fce7f3" },
  dispatch:                { color: "#CA8A04", bg: "#fef3c7" },
  installation:            { color: "#059669", bg: "#d1fae5" },
  review_closeout:         { color: "#4F46E5", bg: "#e0e7ff" },
  completed:               { color: "#15803d", bg: "#dcfce7" },
};

// Role name → short human-friendly label used on the workflow list
const ROLE_LABEL = {
  super_admin: "Super Admin", admin: "Admin",
  sales: "Sales", sales_coordination: "Sales Coord",
  accounts: "Accounts", production_design: "Prod Design",
  production_coordination: "Prod Coord",
  production: "Production", installation: "Installation",
  dealer: "Dealer",
};

export default function Projects() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [projects, setProjects] = useState([]);
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stageFilter, setStageFilter] = useState(searchParams.get("stage") || "");
  const [showFinished, setShowFinished] = useState(searchParams.get("finished") === "1");
  const hasOpenIssueFilter = searchParams.get("has_open_issue") === "1";
  const [search, setSearch] = useState("");
  const [fCustomer, setFCustomer] = useState("");
  const [fProject, setFProject] = useState("");
  const [fSales, setFSales] = useState("");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const listUrl = showFinished ? "/project/finished" : "/project/";
      const [pr, st] = await Promise.all([
        api.get(listUrl, { params: (!showFinished && stageFilter) ? { stage: stageFilter } : {} }),
        api.get("/project/stages"),
      ]);
      setProjects(pr.data); setStages(st.data.stages);
    } catch (e) { /* noop */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [stageFilter, showFinished]);
  useEffect(() => {
    const urlStage = searchParams.get("stage") || "";
    if (urlStage !== stageFilter) setStageFilter(urlStage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  const setStage = (s) => {
    setStageFilter(s);
    if (s) setSearchParams({ stage: s }); else setSearchParams({});
  };

  const reopen = async (p) => {
    const reason = window.prompt(`Reopen "${p.project_id}"?\n\nThis will move the project back to Installation stage and rewind the last completed station. Enter a reason:`, "");
    if (reason === null) return;
    if (!reason.trim()) { toast.error("A reason is required to reopen"); return; }
    try {
      await api.post(`/project/${p.id}/reopen`, { reason: reason.trim() });
      toast.success(`${p.project_id} reopened — back on Installation board`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const counts = stages.reduce((acc, s) => {
    acc[s.key] = projects.filter((p) => p.stage === s.key).length;
    return acc;
  }, {});
  const filtered = useMemo(() => {
    const base = projects.filter((p) => {
      if (search.trim()) {
        const s = search.toLowerCase();
        if (!(
          (p.name || "").toLowerCase().includes(s)
          || (p.customer_name || "").toLowerCase().includes(s)
          || (p.project_id || "").toLowerCase().includes(s)
        )) return false;
      }
      if (fCustomer && !(p.customer_name || "").toLowerCase().includes(fCustomer.toLowerCase())) return false;
      if (fProject && !(p.name || "").toLowerCase().includes(fProject.toLowerCase())) return false;
      if (fSales && !(p.assigned_sales_name || "").toLowerCase().includes(fSales.toLowerCase())) return false;
      // Iter80 — Dashboard's Issues tile deep-links here with
      // ?has_open_issue=1 so users can jump straight to the projects
      // holding open issues.
      if (hasOpenIssueFilter && !p.active_issue_uid && !p.active_issue_tag) return false;
      return true;
    });
    return base.slice().sort((a, b) => {
      // Iter81 — always float rows with the nearest delivery date to the
      // top. Projects that already delivered (stage=completed) or that
      // never had a delivery date get pushed to the bottom.
      const dax = a.estimated_delivery_date && a.stage !== "completed"
                    ? new Date(a.estimated_delivery_date).getTime()
                    : Number.MAX_SAFE_INTEGER;
      const dbx = b.estimated_delivery_date && b.stage !== "completed"
                    ? new Date(b.estimated_delivery_date).getTime()
                    : Number.MAX_SAFE_INTEGER;
      if (dax !== dbx) return dax - dbx;
      // Same (or missing) delivery date → fall back to created_at
      // honouring the user-picked sort direction.
      const ax = new Date(a.created_at || 0).getTime();
      const bx = new Date(b.created_at || 0).getTime();
      return sortDir === "desc" ? bx - ax : ax - bx;
    });
  }, [projects, search, fCustomer, fProject, fSales, sortDir, hasOpenIssueFilter]);
  useEffect(() => { setPage(1); }, [search, fCustomer, fProject, fSales, sortDir, stageFilter, hasOpenIssueFilter]);
  const pageRows = usePaged(filtered, page, 50);

  return (
    <DesktopLayout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="hk-tiny-label" style={{ color: "#E52B50" }}>Projects</div>
          <h1 className="text-4xl font-black tracking-tighter" style={{ fontFamily: "Chivo" }}>
            {showFinished ? "Finished projects" : "Live workflow"}
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            {showFinished
              ? "Successfully completed projects — every station done, every handover signed off."
              : "9-stage pipeline · parallel Procurement track · role-based visibility."}
          </p>
        </div>
        <div className="inline-flex bg-slate-100 rounded-sm p-0.5" data-testid="projects-view-tabs">
          <button type="button" onClick={() => {
              setShowFinished(false);
              const p = new URLSearchParams(searchParams); p.delete("finished"); setSearchParams(p);
            }}
            className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase transition ${
              !showFinished ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"
            }`}
            data-testid="projects-tab-active">
            Active
          </button>
          <button type="button" onClick={() => {
              setShowFinished(true);
              const p = new URLSearchParams(searchParams); p.set("finished", "1"); p.delete("stage"); setSearchParams(p);
              setStageFilter("");
            }}
            className={`px-3 py-1.5 rounded-sm text-xs font-bold uppercase transition ${
              showFinished ? "bg-white shadow-sm text-slate-900" : "text-slate-600 hover:text-slate-900"
            }`}
            data-testid="projects-tab-finished">
            Finished
          </button>
        </div>
      </div>

      {!showFinished && (
      <div className="flex flex-wrap gap-2 mb-4 border-b border-slate-200 pb-3">
        <StageChip label="All" count={projects.length} active={!stageFilter} onClick={() => setStage("")} testId="pstage-all" />
        {stages.map((s) => (
          <StageChip key={s.key} label={s.label} count={counts[s.key] || 0}
            color={STAGE_COLORS[s.key]?.color} bg={STAGE_COLORS[s.key]?.bg}
            active={stageFilter === s.key} onClick={() => setStage(stageFilter === s.key ? "" : s.key)}
            testId={`pstage-${s.key}`} />
        ))}
      </div>
      )}

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <div className="relative flex-1 max-w-sm min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, customer, or project ID"
            data-testid="projects-search"
            className="pl-9 pr-3 py-2 border border-slate-300 rounded-sm text-sm w-full bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none" />
        </div>
        <FilterInput label="Customer" value={fCustomer} onChange={setFCustomer} testId="projects-filter-customer" />
        <FilterInput label="Project name" value={fProject} onChange={setFProject} testId="projects-filter-project" />
        <FilterInput label="Sales person" value={fSales} onChange={setFSales} testId="projects-filter-sales" />
        {(fCustomer || fProject || fSales) && (
          <button type="button" onClick={() => { setFCustomer(""); setFProject(""); setFSales(""); }}
            className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2"
            data-testid="projects-filter-clear">Clear filters</button>
        )}
      </div>

      {loading ? (
        <div className="hk-card p-8 text-center text-slate-500">Loading projects…</div>
      ) : filtered.length === 0 ? (
        <div className="hk-card p-12 text-center text-slate-500" data-testid="projects-empty">
          <Filter className="w-8 h-8 mx-auto text-slate-300 mb-3" />
          <div className="font-bold">No projects yet.</div>
          <p className="text-sm mt-1">Convert an approved quote to create one.</p>
          <Link to="/quotes" className="mt-3 inline-block text-orange-600 hover:text-orange-700 text-sm font-bold">
            Open Quotes →
          </Link>
        </div>
      ) : (
        <div className="hk-card overflow-x-auto">
          <table className="w-full text-sm min-w-[1100px]">
            <thead>
              <tr className="bg-slate-50 hk-tiny-label">
                <th className="px-4 py-3 text-left">Project ID</th>
                <th className="px-4 py-3 text-left min-w-[220px]">Customer / Name</th>
                <th className="px-4 py-3 text-left min-w-[140px]">Sales Person</th>
                <th className="px-4 py-3 text-left min-w-[160px]">Status</th>
                <th className="px-4 py-3 text-left">Stage Owner</th>
                <th className="px-4 py-3 text-left">Pending From</th>
                <th className="px-4 py-3 text-left">
                  <button type="button" onClick={() => setSortDir((d) => (d === "desc" ? "asc" : "desc"))}
                    className="inline-flex items-center gap-1 hover:text-slate-800 uppercase text-[10px] font-bold tracking-wide"
                    data-testid="projects-sort-date">
                    Delivery Date <ArrowUpDown className="w-3 h-3" />
                  </button>
                </th>
                {showFinished && user?.role === "super_admin" && (
                  <th className="px-4 py-3 text-right">Actions</th>
                )}
              </tr>
            </thead>
            <tbody>
              {pageRows.map((p) => {
                const overdue = p.estimated_delivery_date
                  && new Date(p.estimated_delivery_date) < new Date()
                  && p.stage !== "completed";
                const ownerRole = p.stage_owner_role;
                const ownerLabel = ownerRole ? (ROLE_LABEL[ownerRole] || ownerRole.replace(/_/g, " ")) : "—";
                const days = p.pending_days;
                const daysColor = days == null ? "text-slate-500"
                  : days >= 7 ? "text-red-700 font-bold"
                  : days >= 3 ? "text-amber-700 font-semibold"
                  : "text-slate-700";
                // Iter81 — a coloured left-border stripe per row keyed off
                // the project's current pipeline stage so the eye can
                // scan status at a glance.
                const stageMeta = PROJECT_STAGES_BY_KEY[p.stage] || { color: "#94a3b8", bg: "#f1f5f9" };
                return (
                  <tr key={p.id}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    style={{ boxShadow: `inset 4px 0 0 0 ${stageMeta.color}` }}
                    onClick={() => navigate(`/projects/${p.id}`)}
                    data-testid={`project-row-${p.id}`}>
                    <td className="px-4 py-3 font-mono text-xs font-bold text-slate-800 whitespace-normal break-words" data-testid={`proj-id-${p.id}`}>
                      {(p.project_id || "").replace(/^HK\s*-\s*/i, "")}
                    </td>
                    <td className="px-4 py-3 whitespace-normal break-words">
                      <div className="font-bold text-slate-900" data-testid={`proj-customer-${p.id}`}>{p.customer_name || "—"}</div>
                      <div className="text-xs text-slate-500 mt-0.5" data-testid={`proj-name-${p.id}`}>{p.name}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700 whitespace-normal break-words" data-testid={`proj-salesperson-${p.id}`}>
                      {p.assigned_sales_name ? (
                        <span className="inline-flex items-center gap-1">
                          <User className="w-3 h-3 text-slate-400" />
                          {p.assigned_sales_name}
                        </span>
                      ) : (
                        <span className="text-slate-400 italic">— Unassigned —</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {p.active_issue_tag ? (
                        <div data-testid={`proj-issue-${p.id}`}>
                          <span className="text-[10px] font-black uppercase px-2 py-1 rounded-sm bg-rose-50 text-rose-800 border border-rose-200">
                            {p.active_issue_tag}
                          </span>
                          {p.sub_status_reason && (
                            <div className="text-[10px] text-slate-600 mt-0.5 truncate max-w-[200px]" title={p.sub_status_reason}>
                              {p.sub_status_reason}
                            </div>
                          )}
                        </div>
                      ) : p.workflow_status ? (
                        <div className="flex items-center gap-2" data-testid={`proj-workflow-${p.id}`}>
                          <span className="inline-block w-1.5 h-4 rounded-sm shrink-0"
                                style={{ backgroundColor: stageMeta.color }} />
                          <span className="text-[10px] font-black uppercase px-2 py-1 rounded-full whitespace-normal"
                                style={{ color: stageMeta.color, backgroundColor: stageMeta.bg }}>
                            {p.workflow_status}
                          </span>
                        </div>
                      ) : p.status !== "active" ? (
                        <div>
                          <span className="text-[10px] font-black uppercase px-2 py-1 rounded-sm bg-amber-50 text-amber-800 border border-amber-200">
                            {p.status}
                          </span>
                          {p.blocking_reason && (
                            <div className="text-[10px] text-slate-500 mt-0.5 truncate max-w-[200px]" title={p.blocking_reason}>
                              {p.blocking_reason}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-[10px] text-emerald-700 font-bold">Active</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-700 whitespace-nowrap" data-testid={`proj-owner-${p.id}`}>
                      {p.stage_owner_name ? (
                        <>
                          <div className="font-semibold text-slate-800">{p.stage_owner_name}</div>
                          <div className="text-[10px] text-slate-500">{ownerLabel}</div>
                        </>
                      ) : ownerLabel}
                    </td>
                    <td className={`px-4 py-3 text-xs whitespace-nowrap ${daysColor}`} data-testid={`proj-pending-${p.id}`}>
                      {days == null ? "—" : (days === 0 ? "Today" : `${days} day${days === 1 ? "" : "s"}`)}
                    </td>
                    <td className={`px-4 py-3 text-xs whitespace-nowrap ${overdue ? "text-red-700 font-bold" : "text-slate-600"}`} data-testid={`proj-delivery-${p.id}`}>
                      {p.estimated_delivery_date || "—"}
                    </td>
                    {showFinished && user?.role === "super_admin" && (
                      <td className="px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <button type="button" onClick={() => reopen(p)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-sm border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-800 text-[11px] font-bold uppercase"
                          data-testid={`proj-reopen-${p.id}`}>
                          <RotateCcw className="w-3 h-3" /> Reopen
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
          <Pagination total={filtered.length} pageSize={50} page={page} onChange={setPage} testId="projects-pagination" />
        </div>
      )}
    </DesktopLayout>
  );
}

function StageChip({ label, count, active, onClick, color, bg, testId }) {
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-sm text-xs font-bold border transition-colors ${active ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
      <span>{label}</span>
      <span className={`text-[10px] px-1.5 py-0.5 rounded-sm ${active ? "bg-white text-slate-900" : ""}`}
        style={!active && color ? { color, backgroundColor: bg } : (!active ? { backgroundColor: "#f1f5f9" } : undefined)}>
        {count}
      </span>
    </button>
  );
}

function FilterInput({ label, value, onChange, testId }) {
  return (
    <div className="relative">
      <input type="text" value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={label}
        className="bg-white border border-slate-300 rounded-sm pl-3 pr-8 py-1.5 text-xs w-48 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 outline-none"
        data-testid={testId} />
      {value && (
        <button type="button" onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-800 text-xs">×</button>
      )}
    </div>
  );
}
